import { useState } from "react";
import type { CoachingPlaylist, Match } from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import {
  useCoachPlaylistMutations,
  useCoachPlaylistsList,
} from "./hooks/useCoachPlaylistsList";
import { PlaylistComposerModal } from "./components/PlaylistComposerModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Coach > Playlists list page (M6a).
 *
 * Ports the legacy `renderCoachPlaylists` (`js/coaching/
 * playlists.js`): the team's review playlists, each with an item
 * count + the per-row action set.
 *
 * Per-row actions:
 *   - **Preview** → opens the M5 `<FeedbackPlayerModal>` in
 *     `playlist` mode (the M5→M6 carry-in — the
 *     `usePlaylistSession` controller + the modal's playlist mode
 *     shipped in M5; this is the entry point M5 explicitly
 *     deferred here because the M5 bundle had no playlist to
 *     launch from). The modal resolves each item's match
 *     per-item; this page passes the playlist's first item's
 *     match as the fallback.
 *   - **Edit** → the playlist composer modal.
 *   - **Delete** → confirm + `deleteCoachingPlaylist`.
 *
 * Unlike Notes/Clips, the playlist **composer is its own modal**
 * (`<PlaylistComposerModal>`, NEW in M6a) — there's no Coach
 * Review surface for playlists. "+ New playlist" opens it in
 * create mode.
 */

export function PlaylistsPage() {
  const playlistsQuery = useCoachPlaylistsList();
  const mutations = useCoachPlaylistMutations();

  const playlists = playlistsQuery.data?.playlists ?? [];
  const [deleting, setDeleting] = useState<number | null>(null);
  // `composer`: null = closed; { playlist: null } = create;
  // { playlist } = edit.
  const [composer, setComposer] = useState<
    { playlist: CoachingPlaylist | null } | null
  >(null);
  // The playlist currently open in the focused-player preview.
  const [previewPlaylist, setPreviewPlaylist] =
    useState<CoachingPlaylist | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CoachingPlaylist | null>(null);

  const handleDelete = (playlist: CoachingPlaylist) => {
    setConfirmDelete(playlist);
  };

  const confirmDeletePlaylist = async () => {
    if (!confirmDelete) return;
    const playlist = confirmDelete;
    setConfirmDelete(null);
    setDeleting(playlist.id);
    try {
      await mutations.deletePlaylist(playlist.id);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="playlists-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete playlist?"
          message={`Delete "${confirmDelete.title?.trim() || "this playlist"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeletePlaylist}
          onClose={() => setConfirmDelete(null)}
          data-testid="playlists-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Review Playlists"
        description={
          <>
            Stitch notes into a guided session for a player or the team.
            Players watch playlists in <em>My Feedback</em>.
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => setComposer({ playlist: null })}
            data-testid="playlists-new-playlist"
            className="bg-accent hover:bg-accent-hover text-white rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
          >
            + New playlist
          </button>
        }
      />

      {playlistsQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="playlists-loading"
        >
          Loading playlists…
        </p>
      ) : playlists.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="playlists-empty"
        >
          No review playlists yet. Use <strong>+ New playlist</strong> to
          build one from your coaching notes.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="playlists-list">
          {playlists.map((playlist) => (
            <PlaylistRow
              key={playlist.id}
              playlist={playlist}
              deleting={deleting === playlist.id}
              onPreview={() => setPreviewPlaylist(playlist)}
              onEdit={() => setComposer({ playlist })}
              onDelete={() => handleDelete(playlist)}
            />
          ))}
        </ul>
      )}

      {composer ? (
        <PlaylistComposerModal
          playlist={composer.playlist}
          mutations={mutations}
          onClose={() => setComposer(null)}
        />
      ) : null}

      {previewPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: previewPlaylist }}
          // Fallback match — the modal resolves each item's match
          // per-item from `session.currentItem`. The first item's
          // match is the sensible fallback for the no-current-item
          // edge.
          match={firstItemMatch(previewPlaylist)}
          onClose={() => setPreviewPlaylist(null)}
        />
      ) : null}
    </div>
  );
}

/** The fallback `match` for the focused-player modal — the
 * playlist's first item's match (the items are `CoachingNote`s,
 * each carrying `match_id` + `team_id`). Falls back to an empty
 * match shape for an item-less playlist; the modal renders its
 * "no playable items" state in that case anyway. */
function firstItemMatch(
  playlist: CoachingPlaylist,
): Pick<Match, "id" | "team_id"> {
  const first = playlist.items?.[0];
  return {
    id: first?.match_id ?? "",
    team_id: first?.team_id ?? playlist.team_id,
  };
}

type PlaylistRowProps = {
  playlist: CoachingPlaylist;
  deleting: boolean;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

function PlaylistRow({
  playlist,
  deleting,
  onPreview,
  onEdit,
  onDelete,
}: PlaylistRowProps) {
  const itemCount = playlist.items?.length ?? playlist.note_ids?.length ?? 0;
  const metaParts = [
    `${itemCount} ${itemCount === 1 ? "item" : "items"}`,
    playlist.visibility,
    playlist.player_ids?.length
      ? `${playlist.player_ids.length} tagged`
      : "team-wide",
    // The session playback window — legacy `renderCoachPlaylists`
    // surfaced this so a coach can verify pre/post-roll without
    // opening Edit.
    `${playlist.pre_roll_seconds}s pre / ${playlist.post_roll_seconds}s post`,
  ];
  return (
    <li
      className="flex items-center gap-3 rounded-lg border border-rule bg-bg-card px-3 py-2"
      data-testid="playlist-row"
      data-playlist-id={playlist.id}
    >
      <div className="min-w-0 flex-1">
        <div className="font-display text-ink truncate">
          {playlist.title?.trim() || "(untitled playlist)"}
        </div>
        <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
          {metaParts.join(" · ")}
        </div>
        {playlist.description?.trim() ? (
          <div className="text-ink-muted text-xs mt-0.5 truncate">
            {playlist.description.trim()}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <RowButton
          label="Preview"
          testid="playlist-action-preview"
          onClick={onPreview}
          disabled={itemCount === 0}
        />
        <RowButton
          label="Edit"
          testid="playlist-action-edit"
          onClick={onEdit}
        />
        <RowButton
          label="Delete"
          testid="playlist-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
        />
      </div>
    </li>
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "rounded border px-2 py-1 text-xs transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive"
          : "border-rule text-ink-muted hover:text-ink hover:bg-surface",
      ].join(" ")}
    >
      {props.label}
    </button>
  );
}
